import React from "react";
import axios from "axios";
import { Loader2, Crosshair, Shield } from "lucide-react";
import { ARCHETYPES } from "@/components/ArchetypeBadge";
import { atgTitle } from "@/components/AtgChip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEASON = [
  ["GP", "games"], ["G", "goals"], ["A", "assists"], ["PTS", "points"], ["+/-", "plusMinus"],
  ["TOI/G", "timeOnIcePerGame"], ["PPG", "powerPlayGoals"], ["S", "shotsTotal"], ["S%", "shootingPct", 1], ["G/GP", "gpg", 2],
];
const SCORES = [
  ["Team weakness", "team_weakness", "40%"], ["Goalie origin", "goalie_origin", "25%"], ["Exact line", "exact_line", "20%"],
  ["Line / PP", "activation", "10%"], ["Form / env", "form_env", "5%"], ["Season form", "season_form", "±5"],
];
const fmt = (v, dp) => (v == null ? "—" : typeof v === "string" ? v : dp ? Number(v).toFixed(dp) : Math.round(v));

const Cell = ({ label, value, testId, accent }) => (
  <div className="rounded-md border border-slate-800 bg-slate-900/70 px-2 py-1.5" data-testid={testId}>
    <p className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`font-mono text-sm font-bold ${accent || "text-slate-100"}`}>{value}</p>
  </div>
);

const SeasonLine = ({ s }) => (
  <div>
    <p className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-cyan-400">Season line</p>
    <div className="grid grid-cols-5 gap-1.5">
      {SEASON.map(([l, k, dp]) => <Cell key={k} label={l} value={fmt(s?.[k], dp)} testId={`pc-season-${k}`} />)}
    </div>
  </div>
);

const ArchetypeBlock = ({ a }) => {
  const m = ARCHETYPES[a.primary];
  const l5 = a.l5;
  return (
    <div className="rounded-md border border-fuchsia-900/50 bg-fuchsia-950/20 p-2.5" data-testid="pc-archetype">
      <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-fuchsia-400">Archetype · last 5 games</p>
      <p className="font-display text-base font-bold uppercase text-fuchsia-200" data-testid="pc-archetype-name">
        {m ? `${m.icon} ${a.primary}` : a.primary === "UNVERIFIED" ? "No last-5 data" : "No archetype"}
      </p>
      {m && <p className="font-mono text-[10px] text-slate-400">{m.rule} <span className="ml-2 text-fuchsia-300/80">{m.support}</span></p>}
      {l5 && (
        <p className="mt-1 font-mono text-[10px] text-slate-300" data-testid="pc-l5">
          L5: {l5.gp} GP · {l5.g} G · {l5.a} A · {l5.pts} PTS · {l5.sog} SOG ({l5.sog_gp}/GP) · {l5.sh_pct}% SH · {Math.floor(l5.toi_avg / 60)}:{String(l5.toi_avg % 60).padStart(2, "0")} TOI
        </p>
      )}
      <p className="mt-0.5 font-mono text-[10px] text-slate-500">
        Numerology: {a.numerology || "—"}
        {a.day_trend && ` · ${a.day_trend.weekday}s: ${a.day_trend.wd_g} G in ${a.day_trend.wd_gp} GP (${a.day_trend.wd_gpg}/GP vs ${a.day_trend.gpg} overall)`}
      </p>
    </div>
  );
};

const AtgBlock = ({ a }) => (
  <div className="rounded-md border border-emerald-900/50 bg-emerald-950/15 p-2.5" data-testid="pc-atg" title={a ? atgTitle(a) : ""}>
    <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400">Anytime goal odds</p>
    {!a ? <p className="font-mono text-[11px] text-slate-500" data-testid="pc-atg-none">No anytime goal line posted</p> : (
      <>
        <div className="grid grid-cols-4 gap-1.5">
          <Cell label={`Best · ${a.best_book}`} value={a.best} testId="pc-atg-best" accent="text-emerald-300" />
          <Cell label="Implied" value={`${a.implied}%`} testId="pc-atg-implied" />
          <Cell label="Model" value={a.model != null ? `${a.model}%` : "—"} testId="pc-atg-model" accent="text-cyan-300" />
          <Cell label="Edge" value={a.edge != null ? `${a.edge > 0 ? "+" : ""}${a.edge}${a.value ? " VALUE" : ""}` : "—"} testId="pc-atg-edge" accent={a.value ? "text-emerald-300" : a.edge < 0 ? "text-rose-400" : undefined} />
        </div>
        <p className="mt-1.5 font-mono text-[9px] leading-relaxed text-slate-500">{Object.entries(a.books || {}).map(([b, o]) => `${b} ${o}`).join(" · ")}</p>
      </>
    )}
  </div>
);

const Tonight = ({ t }) => {
  if (!t) return <p className="rounded-md border border-slate-800 p-3 text-center font-mono text-[11px] text-slate-500" data-testid="pc-not-playing">Team not on tonight's slate</p>;
  const tg = t.target;
  return (
    <div data-testid="pc-tonight">
      <p className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-rose-400">
        <Crosshair className="h-3 w-3" /> Tonight · {t.game}
      </p>
      <p className="mb-2 flex items-center gap-1.5 font-mono text-[11px] text-slate-400">
        <Shield className="h-3 w-3" /> vs {t.goalie || "TBD"} <span className="text-slate-600">({t.goalie_status || "—"})</span>
      </p>
      {!tg ? (
        <p className="rounded-md border border-amber-900/50 bg-amber-950/20 p-3 font-mono text-[11px] text-amber-300" data-testid="pc-not-in-lineup">
          {t.status === "WATCH" ? t.watch_reason : "Not in tonight's projected lineup (or listed OUT)"}
        </p>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-4 gap-1.5">
            <Cell label="Rank" value={`#${tg.rank} / ${t.team_targets}`} testId="pc-rank" accent="text-rose-300" />
            <Cell label="Score" value={tg.score} testId="pc-score" accent="text-cyan-300" />
            <Cell label="Line slot" value={`${tg.slot}${tg.pp_unit ? ` · ${tg.pp_unit}` : ""}`} testId="pc-slot" />
            <Cell label="Tag" value={tg.tag_label} testId="pc-tag" accent="text-amber-300" />
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            <Cell label="Slot goals vs G" value={`${tg.origin_goals} in ${tg.origin_games}`} testId="pc-origin" />
            <Cell label={`${tg.position} goals vs G`} value={tg.position_goals} testId="pc-pos-goals" />
            <Cell label="First goals" value={tg.first_goal} testId="pc-first" />
            <Cell label="Multi-goal gms" value={tg.multi_goal} testId="pc-multi" />
          </div>
          <div className="rounded-md border border-slate-800 bg-slate-900/50 p-2">
            {SCORES.map(([l, k, w]) => {
              const v = tg.scores?.[k] ?? 0;
              return (
                <div key={k} className="flex items-center gap-2 py-0.5 font-mono text-[10px]" data-testid={`pc-sig-${k}`}>
                  <span className="w-28 text-slate-400">{l}</span>
                  <span className="w-7 text-slate-600">{w}</span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded bg-slate-800">
                    <div className="h-full rounded bg-gradient-to-r from-cyan-500 to-rose-500 transition-[width] duration-500" style={{ width: `${Math.min(v, 100)}%` }} />
                  </div>
                  <span className="w-9 text-right text-slate-200">{Math.round(v)}</span>
                </div>
              );
            })}
          </div>
          {tg.note && <p className="font-mono text-[10px] text-slate-500">{tg.note}</p>}
        </div>
      )}
    </div>
  );
};

export const PlayerCardDialog = ({ player, onClose }) => {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(false);
  React.useEffect(() => {
    if (!player) return;
    setData(null); setErr(false);
    axios.get(`${API}/player-card`, { params: { team: player.team, name: player.name }, timeout: 240000 })
      .then((r) => setData(r.data)).catch(() => setErr(true));
  }, [player]);

  return (
    <Dialog open={!!player} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl border-slate-800 bg-slate-950 text-slate-100" data-testid="player-card-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            {(player?.headshot || data?.season?.headshot) && <img src={player?.headshot || data?.season?.headshot} alt={player?.name} className="h-14 w-14 rounded-full border border-slate-700 bg-slate-900 object-cover" />}
            <div>
              <DialogTitle className="font-display text-xl font-bold uppercase tracking-wide" data-testid="pc-name">{data?.season?.name || player?.name}</DialogTitle>
              <DialogDescription className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{data?.team || player?.team || "—"} · {data?.season?.pos || player?.pos || "—"}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-4">
          <SeasonLine s={data?.season || (player?.games != null ? player : null)} />
          {data?.archetype && <ArchetypeBlock a={data.archetype} />}
          {data && <AtgBlock a={data.atg} />}
          {err ? (
            <p className="font-mono text-[11px] text-rose-400" data-testid="pc-error">Tonight's goal-origin data failed to load</p>
          ) : !data ? (
            <div className="flex items-center gap-2 font-mono text-[11px] text-slate-500" data-testid="pc-loading">
              <Loader2 className="h-4 w-4 animate-spin text-cyan-500" /> Loading tonight's goal-origin targets…
            </div>
          ) : <Tonight t={data.tonight} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlayerCardDialog;

const PlayerCardCtx = React.createContext(() => {});

export const PlayerCardProvider = ({ children }) => {
  const [player, setPlayer] = React.useState(null);
  return (
    <PlayerCardCtx.Provider value={setPlayer}>
      {children}
      <PlayerCardDialog player={player} onClose={() => setPlayer(null)} />
    </PlayerCardCtx.Provider>
  );
};

export const PlayerName = ({ name, team, className = "", testId, children }) => {
  const open = React.useContext(PlayerCardCtx);
  if (!name) return children || null;
  return (
    <span
      role="button"
      tabIndex={0}
      data-testid={testId || `player-link-${(team || "x").toLowerCase()}-${String(name).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      onClick={(e) => { e.stopPropagation(); open({ name, team }); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); open({ name, team }); } }}
      className={`cursor-pointer text-left underline-offset-2 transition-colors hover:text-cyan-300 hover:underline ${className}`}
    >
      {children || name}
    </span>
  );
};
